import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { PanelProduct } from '@storeo/api-client';
import { MSym } from '@storeo/ui';
import { api } from '../lib/api';
import { money } from '../lib/format';
import { PlaceholderTag } from '../components/lumina/Placeholder';

const TYPE_LABEL: Record<string, string> = {
  one_time: 'Pagamento único',
  subscription: 'Assinatura mensal',
};

const CHANNEL_LABEL: Record<string, string> = {
  digital_file: 'Arquivo para download',
  digital_link: 'Link ou instrução',
  video: 'Vídeo (área do cliente)',
  telegram: 'Acesso a grupo do Telegram',
  physical: 'Produto físico (eu envio)',
};

const FIELD =
  'w-full h-11 px-space-md rounded-lg bg-surface-container-lowest text-on-surface font-body-md text-body-md placeholder:text-outline focus:outline-none focus:bg-surface-container transition-all shadow-inner';
const LABEL = 'font-label-sm text-label-sm text-on-surface-variant font-medium';

/** Produtos & Estoque — redesign Lumina (docs/prototype/storeo_produtos_gest_o_de_estoque).
 *  Dado real: products + CRUD + publish. SKU/estoque/order-bump/reposição = placeholder. */
export function Produtos() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const products = useQuery({ queryKey: ['products'], queryFn: () => api.products() });
  const editingProduct = products.data?.find((p) => p.id === editingId) ?? null;

  const list = products.data ?? [];
  const published = list.filter((p) => p.status === 'published').length;

  const togglePublish = useMutation({
    mutationFn: (p: PanelProduct) => (p.status === 'published' ? api.unpublishProduct(p.id) : api.publishProduct(p.id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });

  return (
    <div className="lumina flex flex-col gap-space-lg text-on-surface">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-space-md">
        <div className="flex flex-col gap-space-3xs">
          <div className="flex items-center gap-space-xs">
            <span className="font-label-sm text-label-sm uppercase tracking-wider text-primary font-bold">Operação &amp; Catálogo</span>
            <span className="text-outline text-label-sm">•</span>
            <span className="font-label-sm text-label-sm text-secondary flex items-center gap-space-3xs">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary" /> Sincronizado
            </span>
          </div>
          <h1 className="font-headline-lg text-headline-lg text-on-surface tracking-tight">Produtos &amp; Estoque</h1>
          <p className="font-body-md text-body-md text-on-surface-variant">
            O que você vende — preço, tipo de cobrança e como o cliente recebe.
          </p>
        </div>
        <button
          onClick={() => {
            setCreating((v) => !v);
            setEditingId(null);
          }}
          className="flex items-center gap-space-xs px-space-lg py-space-xs rounded-xl bg-primary text-on-primary hover:bg-primary-fixed-dim transition-all shadow-[0_0_24px_-4px_rgba(192,193,255,0.45)] font-label-lg text-label-lg self-start"
        >
          <MSym name={creating ? 'close' : 'add'} className="text-[20px]" />
          <span>{creating ? 'Fechar' : 'Novo Produto'}</span>
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-space-md">
        <Kpi label="Catálogo Ativo" value={String(list.length)} icon="category" wrap="text-primary" />
        <Kpi label="Publicados" value={String(published)} icon="visibility" wrap="text-secondary" />
        <Kpi label="Rascunhos" value={String(list.length - published)} icon="edit_note" wrap="text-tertiary" />
        <Kpi label="Estoque Crítico" value="3" icon="warning" wrap="text-error" placeholder />
      </div>

      {creating ? (
        <div className="rounded-xl bg-surface-container-low p-space-lg shadow-md">
          <ProductForm mode="create" onDone={() => setCreating(false)} />
        </div>
      ) : null}
      {editingProduct ? (
        <div className="rounded-xl bg-surface-container-low p-space-lg shadow-md">
          <ProductForm mode="edit" product={editingProduct} onDone={() => setEditingId(null)} />
        </div>
      ) : null}

      {/* table */}
      <div className="rounded-2xl bg-surface-container overflow-hidden shadow-lg">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse font-body-sm text-body-sm">
            <thead>
              <tr className="bg-surface-container-highest/40 text-on-surface font-label-sm text-label-sm uppercase tracking-wider font-semibold">
                <th className="py-space-md px-space-lg">Produto</th>
                <th className="py-space-md px-space-md">Cobrança</th>
                <th className="py-space-md px-space-md">Entrega</th>
                <th className="py-space-md px-space-md text-right">Preço</th>
                <th className="py-space-md px-space-md">Status</th>
                <th className="py-space-md px-space-lg text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="text-on-surface">
              {products.isLoading ? (
                <tr>
                  <td colSpan={6} className="py-space-2xl text-center text-on-surface-variant">Carregando…</td>
                </tr>
              ) : list.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-space-2xl text-center text-on-surface-variant">
                    Nenhum produto ainda — crie o primeiro.
                  </td>
                </tr>
              ) : (
                list.map((p) => (
                  <tr key={p.id} className="hover:bg-surface-container-high/60 transition-colors border-t border-surface-container-highest/30">
                    <td className="py-space-md px-space-lg">
                      <div className="flex items-center gap-space-sm">
                        <span className="w-10 h-10 rounded-lg bg-linear-to-br from-primary-container/40 to-tertiary-container/40 shrink-0 flex items-center justify-center text-on-surface-variant">
                          <MSym name={p.deliveryChannel === 'physical' ? 'inventory_2' : 'bolt'} className="text-[20px]" />
                        </span>
                        <div className="min-w-0">
                          <span className="font-label-md text-label-md font-bold text-on-surface block truncate">{p.name}</span>
                          <span className="font-label-sm text-label-sm text-outline">/p/{p.slug}</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-space-md px-space-md text-on-surface-variant">{TYPE_LABEL[p.type] ?? p.type}</td>
                    <td className="py-space-md px-space-md text-on-surface-variant">{CHANNEL_LABEL[p.deliveryChannel] ?? p.deliveryChannel}</td>
                    <td className="py-space-md px-space-md text-right font-label-md text-label-md font-extrabold text-on-surface whitespace-nowrap">
                      {money(p.amount)}
                    </td>
                    <td className="py-space-md px-space-md">
                      <span
                        className={`px-2 py-0.5 rounded-full font-label-sm text-label-sm font-bold ${
                          p.status === 'published' ? 'bg-secondary-container/25 text-secondary' : 'bg-surface-variant text-outline'
                        }`}
                      >
                        {p.status === 'published' ? 'Publicado' : 'Rascunho'}
                      </span>
                    </td>
                    <td className="py-space-md px-space-lg text-right">
                      <div className="inline-flex gap-space-2xs">
                        <button
                          className="px-space-sm py-1 rounded-lg bg-surface-container-high hover:bg-surface-variant text-on-surface font-label-sm text-label-sm font-semibold transition-colors"
                          onClick={() => {
                            setEditingId(p.id);
                            setCreating(false);
                          }}
                        >
                          Editar
                        </button>
                        <Link
                          to={`/produtos/${p.id}`}
                          className="px-space-sm py-1 rounded-lg bg-surface-container-high hover:bg-surface-variant text-on-surface font-label-sm text-label-sm font-semibold transition-colors"
                        >
                          Entrega
                        </Link>
                        <button
                          className="px-space-sm py-1 rounded-lg bg-surface-container-high hover:bg-surface-variant text-on-surface font-label-sm text-label-sm font-semibold transition-colors disabled:opacity-50"
                          disabled={togglePublish.isPending}
                          onClick={() => togglePublish.mutate(p)}
                        >
                          {p.status === 'published' ? 'Despublicar' : 'Publicar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* bottom — order bump strategy (placeholder) */}
      <div className="rounded-2xl bg-surface-container-low p-space-lg shadow-md flex flex-col md:flex-row md:items-center justify-between gap-space-md">
        <div className="flex items-center gap-space-sm">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <MSym name="bolt" className="text-[22px]" />
          </div>
          <div>
            <span className="font-label-lg text-label-lg text-on-surface font-bold flex items-center">
              Estratégia de Order Bump <PlaceholderTag />
            </span>
            <span className="font-body-sm text-body-sm text-on-surface-variant">
              Aumente o ticket médio oferecendo um complemento no checkout. Em breve.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({
  label,
  value,
  icon,
  wrap,
  placeholder,
}: {
  label: string;
  value: string;
  icon: string;
  wrap: string;
  placeholder?: boolean;
}) {
  return (
    <div className="p-space-lg rounded-xl bg-surface-container-low flex flex-col justify-between relative overflow-hidden group hover:bg-surface-container transition-all shadow-md">
      <div className="flex items-start justify-between">
        <div className="flex flex-col">
          <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider flex items-center">
            {label}
            {placeholder ? <PlaceholderTag /> : null}
          </span>
          <span className={`font-display-xl text-display-xl font-extrabold mt-space-2xs ${placeholder ? 'text-error' : 'text-on-surface'}`}>
            {value}
          </span>
        </div>
        <div className={`w-10 h-10 rounded-xl bg-surface-container-high flex items-center justify-center group-hover:scale-110 transition-transform ${wrap}`}>
          <MSym name={icon} className="text-[22px]" />
        </div>
      </div>
    </div>
  );
}

const DIACRITICS = /[̀-ͯ]/g;
function slugify(v: string): string {
  return v
    .normalize('NFD')
    .replace(DIACRITICS, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

type ProductFormProps =
  | { mode: 'create'; onDone: () => void }
  | { mode: 'edit'; product: PanelProduct; onDone: () => void };

function ProductForm(props: ProductFormProps) {
  const qc = useQueryClient();
  const editing = props.mode === 'edit' ? props.product : null;
  const [name, setName] = useState(editing?.name ?? '');
  const [slug, setSlug] = useState(editing?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState(editing?.description ?? '');
  const [type, setType] = useState(editing?.type ?? 'one_time');
  const [amount, setAmount] = useState(editing ? String(editing.amount) : '');
  const [deliveryChannel, setDeliveryChannel] = useState(editing?.deliveryChannel ?? 'digital_file');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const details = {
        name,
        description: description || undefined,
        type,
        amount: Number(amount.replace(',', '.')),
        deliveryChannel,
      };
      if (props.mode === 'edit') await api.updateProduct(props.product.id, details);
      else await api.createProduct({ ...details, slug });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      props.onDone();
    },
    onError: (e: unknown) =>
      setError(e instanceof Error ? e.message : `Não foi possível ${props.mode === 'edit' ? 'salvar' : 'criar'} o produto`),
  });

  return (
    <form
      className="flex flex-col gap-space-md"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        save.mutate();
      }}
    >
      <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">
        {props.mode === 'edit' ? 'Editar produto' : 'Novo produto'}
      </h2>

      <label className="flex flex-col gap-space-2xs">
        <span className={LABEL}>Nome</span>
        <input
          className={FIELD}
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (props.mode === 'create' && !slugTouched) setSlug(slugify(e.target.value));
          }}
        />
      </label>

      {props.mode === 'create' ? (
        <label className="flex flex-col gap-space-2xs">
          <span className={LABEL}>Endereço (slug)</span>
          <input
            className={FIELD}
            required
            value={slug}
            onChange={(e) => {
              setSlug(slugify(e.target.value));
              setSlugTouched(true);
            }}
          />
          <span className="font-label-sm text-label-sm text-outline">Aparece na URL da loja: /p/{slug || 'seu-produto'}</span>
        </label>
      ) : (
        <p className="font-label-sm text-label-sm text-outline">Endereço: /p/{slug} — não muda depois de criado.</p>
      )}

      <label className="flex flex-col gap-space-2xs">
        <span className={LABEL}>Descrição</span>
        <textarea className={`${FIELD} h-auto py-space-sm`} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-md">
        <label className="flex flex-col gap-space-2xs">
          <span className={LABEL}>Cobrança</span>
          <select className={FIELD} value={type} onChange={(e) => setType(e.target.value)}>
            {Object.entries(TYPE_LABEL).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-space-2xs">
          <span className={LABEL}>Preço (R$)</span>
          <input className={FIELD} required inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </label>
      </div>

      <label className="flex flex-col gap-space-2xs">
        <span className={LABEL}>Como o cliente recebe?</span>
        <select className={FIELD} value={deliveryChannel} onChange={(e) => setDeliveryChannel(e.target.value)}>
          {Object.entries(CHANNEL_LABEL).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </label>

      {error ? (
        <p className="rounded-lg bg-error-container/25 text-on-error-container px-space-md py-space-sm font-body-sm text-body-sm">{error}</p>
      ) : null}
      <div className="flex gap-space-sm">
        <button
          className="px-space-lg py-space-xs rounded-xl bg-primary text-on-primary font-label-lg text-label-lg font-bold disabled:opacity-60"
          type="submit"
          disabled={save.isPending}
        >
          {save.isPending ? 'Salvando…' : props.mode === 'edit' ? 'Salvar alterações' : 'Criar produto'}
        </button>
        <button
          type="button"
          onClick={props.onDone}
          className="px-space-lg py-space-xs rounded-xl bg-surface-container-high text-on-surface font-label-lg text-label-lg"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
